// Google Drive 公開資料夾 → 照片清單
// 走 Drive Files API v3 with API key（無需 OAuth）。
// 資料夾必須設為「知道連結的任何人 - 檢視者」才能讀。

const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;
const DRIVE_API = "https://www.googleapis.com/drive/v3/files";

// Drive thumbnailLink 預設是 small (220px)，我們改成大一點
function upgradeThumbSize(link, size) {
  if (!link) return link;
  return link.replace(/=s\d+(-[a-z]+)?$/, `=s${size}`);
}

// 直接用 Google 的 lh3 endpoint 拿大圖
// =w 限定寬度 + 自動算 aspect ratio（横向直向都完整）
// 比 Drive thumbnailLink 穩定，且不會被裁切
function lh3LargeUrl(fileId, size) {
  return `https://lh3.googleusercontent.com/d/${fileId}=w${size}`;
}

// 列資料夾內所有圖片（公開分享 folder 才能列）
export async function listDriveFolder(folderId) {
  if (!API_KEY) {
    console.warn("[drive] VITE_GOOGLE_API_KEY 未設定，跳過 Drive source");
    return [];
  }
  if (!folderId) return [];

  const q = encodeURIComponent(
    `'${folderId}' in parents and (mimeType contains 'image/' or mimeType contains 'video/') and trashed = false`,
  );
  const fields = encodeURIComponent(
    "files(id,name,mimeType,thumbnailLink,createdTime,imageMediaMetadata(time,width,height),webViewLink,size)",
  );
  const url = `${DRIVE_API}?q=${q}&fields=${fields}&pageSize=200&orderBy=createdTime&key=${API_KEY}&_t=${Date.now()}`;

  try {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.warn("[drive] list failed", res.status, err);
      return [];
    }
    const data = await res.json();
    return (data.files || []).map((f) => normalizeDriveFile(f, folderId));
  } catch (e) {
    console.warn("[drive] fetch error", e);
    return [];
  }
}

// 把 Drive 檔案 metadata 轉成跟 R2 manifest 同構的 photo entry
function normalizeDriveFile(file, folderId) {
  const exifTime =
    file.imageMediaMetadata?.time && parseDriveExifTime(file.imageMediaMetadata.time);
  const exifDate = exifTime || file.createdTime || null;

  // 縮圖：用 thumbnailLink (CDN cache 快) → fallback Drive thumbnail API
  const thumb = upgradeThumbSize(file.thumbnailLink, 600)
    || `https://drive.google.com/thumbnail?id=${file.id}&sz=w600`;
  // Lightbox medium：直接用 lh3 endpoint 拿原始解析度（thumbnailLink 升級到 1920 仍可能被裁切）
  const medium = lh3LargeUrl(file.id, 2400);

  return {
    id: `drive-${file.id}`,
    source: "drive",
    driveFileId: file.id,
    driveFolderId: folderId,
    exifDate: exifDate,
    thumb,
    medium,
    original: `https://drive.google.com/uc?export=download&id=${file.id}`,
    viewLink: file.webViewLink || `https://drive.google.com/file/d/${file.id}/view`,
    uploader: null, // Drive 不主動帶上傳者（除非用 OAuth）
    uploadedAt: file.createdTime,
    originalName: file.name,
    mimeType: file.mimeType,
    size: file.size ? Number(file.size) : null,
  };
}

// "2026:05:03 13:30:45" → ISO
function parseDriveExifTime(s) {
  if (!s) return null;
  const m = s.match(/^(\d{4}):(\d{2}):(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (!m) return s;
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`;
}
